import {
  Decoration,
  DecorationSet,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  ViewUpdate,
} from "@codemirror/view";

/**
 * Rough Ruby highlighter:
 * - Keywords in red (`cm-ruby-keyword`)
 * - Symbols, strings, and numeric literals in blue (`cm-ruby-accent`)
 * - Class/constant-ish names (capitalized terms) in green (`cm-ruby-class`)
 * - Comments and `%{ ... }` blocks in gray (`cm-ruby-comment`)
 *
 * Note: Comments and `%{ ... }` ranges get `cm-ruby-comment`, which
 * visually overrides other colors inside them, effectively “removing”
 * other highlights within comments.
 */
export function rubyKeywordHighlightExtension() {
  const keywordDecoration = Decoration.mark({ class: "cm-ruby-keyword" });
  const accentDecoration = Decoration.mark({ class: "cm-ruby-accent" });
  const classDecoration = Decoration.mark({ class: "cm-ruby-class" });
  const commentDecoration = Decoration.mark({ class: "cm-ruby-comment" });

  // Basic set of Ruby reserved words
  const keywordDecorator = new MatchDecorator({
    regexp:
      /\b(?:def|class|module|if|elsif|else|end|do|while|until|case|when|then|yield|return|self|super|nil|true|false|and|or|not|begin|rescue|ensure|unless|redo|retry|break|next)\b/g,
    decoration: () => keywordDecoration,
  });

  // Symbols (:foo or foo:) + string literals + number literals
  const accentDecorator = new MatchDecorator({
    regexp:
      /(:[a-zA-Z_][a-zA-Z0-9_]*|[a-zA-Z_][a-zA-Z0-9_]*:|"[^"\n]*"|'[^\n']*'|\b\d+(?:\.\d+)?\b)/g,
    decoration: () => accentDecoration,
  });

  // Class/constant names: terms starting with a capital letter
  const classDecorator = new MatchDecorator({
    regexp: /\b[A-Z][A-Za-z0-9_]*\b/g,
    decoration: () => classDecoration,
  });

  // Single-line comments: everything from '#' to end of line
  const commentLineDecorator = new MatchDecorator({
    regexp: /#[^\n]*/g,
    decoration: () => commentDecoration,
  });

  const keywordPlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = keywordDecorator.createDeco(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = keywordDecorator.updateDeco(update, this.decorations);
        }
      }
    },
    { decorations: (v) => v.decorations }
  );

  const accentPlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = accentDecorator.createDeco(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = accentDecorator.updateDeco(update, this.decorations);
        }
      }
    },
    { decorations: (v) => v.decorations }
  );

  const classPlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = classDecorator.createDeco(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = classDecorator.updateDeco(update, this.decorations);
        }
      }
    },
    { decorations: (v) => v.decorations }
  );

  const commentLinePlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = commentLineDecorator.createDeco(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = commentLineDecorator.updateDeco(update, this.decorations);
        }
      }
    },
    { decorations: (v) => v.decorations }
  );

  // `%{ ... }` blocks (rough, non-nested) – mark entire block as comment/ignored
  const percentBlockPlugin = ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = this.buildDecos(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = this.buildDecos(update.view);
        }
      }

      buildDecos(view: EditorView): DecorationSet {
        const decos: ReturnType<Decoration["range"]>[] = [];
        const text = view.state.doc.toString();
        const re = /%\{[\s\S]*?\}/g;
        let m: RegExpExecArray | null;
        while ((m = re.exec(text)) !== null) {
          const from = m.index;
          const to = m.index + m[0].length;
          decos.push(commentDecoration.range(from, to));
        }
        return Decoration.set(decos);
      }
    },
    { decorations: (v) => v.decorations }
  );

  // Return all plugins as a single extension group
  return [keywordPlugin, accentPlugin, classPlugin, commentLinePlugin, percentBlockPlugin];
}

